import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, MapPin, Users, Pencil, Trash2, Link as LinkIcon } from 'lucide-react';
import { propertiesAPI, bookingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import toast from 'react-hot-toast';

const PLAN_ORDER = ['free', 'basic', 'pro', 'agency'];
const UPGRADE_PLANS = [
  { id: 'basic',  name: 'Basic',  price: 149, limit: 'Up to 3 properties' },
  { id: 'pro',    name: 'Pro',    price: 299, limit: 'Up to 10 properties', popular: true },
  { id: 'agency', name: 'Agency', price: 499, limit: 'Unlimited properties' },
];

function PropertyModal({ property, onClose, onSave, onPlanLimitExceeded }) {
  const [form, setForm] = useState(property || { name:'', address:'', city:'', property_type:'apartment', bedrooms:1, max_guests:2, price_per_night:'' });
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();
  const set = (k, v) => setForm(f => ({...f, [k]:v}));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (property) { await propertiesAPI.update(property.id, form); toast.success(t('properties.edit') + '!'); }
      else { await propertiesAPI.create(form); toast.success(t('properties.addNew') + '!'); }
      onSave();
    } catch (err) {
      if (err.response?.data?.error === 'plan_limit_exceeded') {
        onClose();
        onPlanLimitExceeded(err.response.data);
      } else {
        toast.error(err.response?.data?.message || 'Error saving');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 style={{ fontFamily:'Syne', fontWeight:700 }}>{property ? t('properties.edit') : t('properties.addNew')}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:20 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>{t('properties.name')}</label>
              <input placeholder={t('properties.namePlaceholder')} value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label>{t('properties.city')}</label>
                <input placeholder={t('properties.cityPlaceholder')} value={form.city} onChange={e => set('city', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>{t('properties.type')}</label>
                <select value={form.property_type} onChange={e => set('property_type', e.target.value)}>
                  <option value="apartment">{t('properties.type.apartment')}</option>
                  <option value="villa">{t('properties.type.villa')}</option>
                  <option value="riad">{t('properties.type.riad')}</option>
                  <option value="studio">{t('properties.type.studio')}</option>
                  <option value="house">{t('properties.type.house')}</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{t('properties.address')}</label>
              <input placeholder={t('properties.addressPlaceholder')} value={form.address} onChange={e => set('address', e.target.value)} required />
            </div>
            <div className="grid-3">
              <div className="form-group"><label>{t('properties.bedrooms')}</label><input type="number" min="0" value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)} /></div>
              <div className="form-group"><label>{t('properties.maxGuests')}</label><input type="number" min="1" value={form.max_guests} onChange={e => set('max_guests', e.target.value)} /></div>
              <div className="form-group"><label>{t('properties.priceNight')}</label><input type="number" min="0" placeholder="350" value={form.price_per_night} onChange={e => set('price_per_night', e.target.value)} /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-ghost">{t('action.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="spinner" /> : property ? t('action.save') : t('properties.addNew')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UpgradeModal({ info, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const currentPlan = info.current_plan || user?.subscription_plan || 'free';
  const currentIndex = PLAN_ORDER.indexOf(currentPlan);
  const plans = UPGRADE_PLANS.filter(p => PLAN_ORDER.indexOf(p.id) > currentIndex);
  const planLabel = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div>
            <h3 style={{ fontFamily: 'Syne', fontWeight: 700 }}>Upgrade your plan</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
              Your <strong style={{ color: 'var(--text-secondary)' }}>{planLabel}</strong> plan is limited to{' '}
              <strong style={{ color: 'var(--text-secondary)' }}>
                {info.plan_limit} {info.plan_limit === 1 ? 'property' : 'properties'}
              </strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:22, alignSelf:'flex-start', lineHeight:1 }}>×</button>
        </div>

        <div className="modal-body" style={{ paddingTop: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {plans.map(plan => (
              <div
                key={plan.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 18px',
                  borderRadius: 12,
                  border: `1px solid ${plan.popular ? 'var(--accent)' : 'var(--border)'}`,
                  background: plan.popular ? 'rgba(108,99,255,0.08)' : 'var(--bg)',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 }}>
                    {plan.name}
                    {plan.popular && (
                      <span className="badge badge-accent" style={{ fontSize: 10, padding: '2px 8px' }}>Popular</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{plan.limit}</div>
                </div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 18, color: plan.popular ? 'var(--accent-light)' : 'var(--text-primary)', textAlign: 'right' }}>
                  {plan.price}
                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', display: 'block' }}>MAD/mo</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-ghost">Maybe later</button>
          <button onClick={() => { onClose(); navigate('/pricing'); }} className="btn btn-primary">
            View all plans →
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(null);
  const { t } = useLanguage();

  const load = () => propertiesAPI.getAll().then(r => setProperties(r.data.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('properties.confirmRemove'))) return;
    await propertiesAPI.remove(id);
    toast.success('✓');
    load();
  };

  const handleGenerateLink = async (id) => {
    try {
      const res = await bookingsAPI.generateLink(id);
      await navigator.clipboard.writeText(res.data.data.link);
      toast.success('Booking link copied!');
    } catch { toast.error('Failed to generate link'); }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <span className="spinner" style={{ width:32, height:32, borderWidth:3 }} />
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 }}>
        <div>
          <h1 style={{ fontSize:28, fontWeight:800, marginBottom:4 }}>{t('properties.title')}</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:14 }}>{t('properties.count', properties.length)}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={16} /> {t('action.addProperty')}</button>
      </div>

      {properties.length === 0 ? (
        <div className="empty-state card">
          <Building2 size={48} />
          <h3>{t('properties.empty')}</h3>
          <p>{t('properties.emptyDesc')}</p>
          <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={16} /> {t('properties.addFirst')}</button>
        </div>
      ) : (
        <div className="grid-3">
          {properties.map(p => (
            <div key={p.id} className="card">
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:'var(--accent-glow)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Building2 size={20} color="var(--accent-light)" />
                </div>
                <div style={{ display:'flex', gap:4 }}>
                  <button onClick={() => setModal(p)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:6, borderRadius:8 }}><Pencil size={15} /></button>
                  <button onClick={() => handleGenerateLink(p.id)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:6, borderRadius:8 }}><LinkIcon size={15} /></button>
                  <button onClick={() => handleDelete(p.id)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:6, borderRadius:8 }}><Trash2 size={15} /></button>
                </div>
              </div>
              <h3 style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>{p.name}</h3>
              <div style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-muted)', fontSize:13, marginBottom:16 }}>
                <MapPin size={13} /> {p.city}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div style={{ background:'var(--bg)', borderRadius:10, padding:'10px 14px' }}>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>{t('properties.thisMonth')}</div>
                  <div style={{ fontFamily:'Syne', fontWeight:700, color:'var(--gold)' }}>{Number(p.income_this_month||0).toLocaleString()} MAD</div>
                </div>
                <div style={{ background:'var(--bg)', borderRadius:10, padding:'10px 14px' }}>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>{t('properties.activeGuests')}</div>
                  <div style={{ fontFamily:'Syne', fontWeight:700, color:'var(--success)' }}>{p.active_bookings||0}</div>
                </div>
              </div>
              <div style={{ marginTop:12, display:'flex', gap:8, flexWrap:'wrap' }}>
                <span className="badge badge-accent" style={{ fontSize:11 }}>{t('properties.type.' + p.property_type) || p.property_type}</span>
                <span className="badge" style={{ background:'var(--bg)', color:'var(--text-muted)', fontSize:11 }}><Users size={11} /> {p.max_guests} {t('properties.max')}</span>
                {p.price_per_night && <span className="badge" style={{ background:'var(--bg)', color:'var(--text-muted)', fontSize:11 }}>{p.price_per_night} MAD/night</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <PropertyModal
          property={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
          onPlanLimitExceeded={(info) => { setModal(null); setUpgradeModal(info); }}
        />
      )}
      {upgradeModal && <UpgradeModal info={upgradeModal} onClose={() => setUpgradeModal(null)} />}
    </div>
  );
}
